// Modern Google Places Autocomplete (manual control, no deprecated widget)
//
// Why this rewrite (May 7, 2026): Google deprecated `google.maps.places.Autocomplete`
// (the "magic widget" that injects a `.pac-container` dropdown into <body>) for
// **new Google Cloud projects** as of March 1, 2025. For existing projects it
// still works, but for any newly-issued API key the widget silently fails to
// render its dropdown — exactly what was happening in the Smart Estimator
// "Create Customer" dialog.
//
// Instead we call the still-supported `AutocompleteService.getPlacePredictions`
// + `PlacesService.getDetails` APIs directly and render our own dropdown
// inside our React tree. Benefits:
//  - Works with both legacy AND new Google projects
//  - No more `.pac-container` z-index battles inside Radix Dialog
//  - Full styling control (matches the rest of OC Tasker)
//  - Keyboard navigation (↑/↓/Enter/Esc)
//
// API is unchanged so all existing usages (AuthModal, GuestCheckoutForm,
// TVMountingCalculator, PressureWashingCalculator, SmartEstimatePage) keep
// working without edits.
import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { loadGoogleMapsScript } from "@/lib/googleMapsLoader";

const SESSION_DEBOUNCE_MS = 180;

const parseAddressComponents = (place, fallbackText = "") => {
  const data = {
    formatted_address: place?.formatted_address || fallbackText || "",
    street_number: "",
    street_name: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
  };
  for (const c of place?.address_components || []) {
    const t = c.types || [];
    if (t.includes("street_number")) data.street_number = c.long_name;
    if (t.includes("route")) data.street_name = c.long_name;
    if (t.includes("locality")) data.city = c.long_name;
    if (!data.city && t.includes("sublocality")) data.city = c.long_name;
    if (t.includes("administrative_area_level_1")) data.state = c.short_name;
    if (t.includes("postal_code")) data.zip_code = c.long_name;
    if (t.includes("country")) data.country = c.short_name;
  }
  data.street_address =
    [data.street_number, data.street_name].filter(Boolean).join(" ") ||
    (data.formatted_address.split(",")[0] || "").trim();
  return data;
};

const AddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Start typing your address...",
  className = "",
  required = false,
  id,
  name,
  ...props
}) => {
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const placesServiceRef = useRef(null);
  const placesServiceDivRef = useRef(null);

  const [displayValue, setDisplayValue] = useState(value ?? "");
  const [predictions, setPredictions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [apiReady, setApiReady] = useState(false);

  // Sync external value prop → internal display
  useEffect(() => {
    if (value !== undefined && value !== displayValue) {
      setDisplayValue(value);
    }
  }, [value, displayValue]);

  // Lazy-inject Google Maps + Places on first mount and wait for readiness.
  // The script is no longer preloaded in index.html — we only pay the
  // 1.25 MB JS cost when an autocomplete input actually appears.
  useEffect(() => {
    let mounted = true;
    let timer;
    const markReady = () => {
      if (!mounted) return;
      setApiReady(true);
      try {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      } catch { /* ignore */ }
    };
    if (window.google?.maps?.places) {
      markReady();
    } else {
      loadGoogleMapsScript()
        .then(() => {
          // The Places library sometimes lags one tick after script load.
          const poll = () => {
            if (!mounted) return;
            if (window.google?.maps?.places) markReady();
            else timer = setTimeout(poll, 100);
          };
          poll();
        })
        .catch(() => { /* silent — user can still type, just no suggestions */ });
    }
    return () => { mounted = false; if (timer) clearTimeout(timer); };
  }, []);

  // Lazy-create the PlacesService (needs a DOM element host)
  const ensurePlacesService = () => {
    if (placesServiceRef.current) return placesServiceRef.current;
    if (!window.google?.maps?.places) return null;
    if (!placesServiceDivRef.current) {
      placesServiceDivRef.current = document.createElement("div");
    }
    placesServiceRef.current = new window.google.maps.places.PlacesService(
      placesServiceDivRef.current
    );
    return placesServiceRef.current;
  };

  const fetchPredictions = useMemo(() => (text) => {
    if (!apiReady || !text || text.trim().length < 3) {
      setPredictions([]);
      setIsOpen(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const svc = new window.google.maps.places.AutocompleteService();
      svc.getPlacePredictions(
        {
          input: text,
          componentRestrictions: { country: "us" },
          types: ["address"],
          sessionToken: sessionTokenRef.current,
        },
        (results, status) => {
          setLoading(false);
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results) {
            setPredictions([]);
            setIsOpen(false);
            return;
          }
          setPredictions(results.slice(0, 5));
          setIsOpen(true);
          setHighlight(-1);
        },
      );
    } catch (e) {
      console.error("Places prediction error:", e);
      setLoading(false);
      setPredictions([]);
      setIsOpen(false);
    }
  }, [apiReady]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setDisplayValue(v);
    if (onChange) onChange(e);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(v), SESSION_DEBOUNCE_MS);
  };

  const selectPrediction = (pred) => {
    if (!pred?.place_id) return;
    const svc = ensurePlacesService();
    if (!svc) return;
    svc.getDetails(
      {
        placeId: pred.place_id,
        fields: ["address_components", "formatted_address", "geometry"],
        sessionToken: sessionTokenRef.current,
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          // Fallback: use the prediction text directly
          const addr = parseAddressComponents(null, pred.description);
          setDisplayValue(addr.street_address || pred.description);
          setIsOpen(false);
          setPredictions([]);
          if (onPlaceSelect) onPlaceSelect(addr);
          return;
        }
        const addr = parseAddressComponents(place, pred.description);
        setDisplayValue(addr.street_address || addr.formatted_address);
        setIsOpen(false);
        setPredictions([]);
        if (onPlaceSelect) onPlaceSelect(addr);
        // New session token for the next address
        try {
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        } catch { /* ignore */ }
      },
    );
  };

  const handleKeyDown = (e) => {
    if (!isOpen || predictions.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, predictions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      const pick = highlight >= 0 ? predictions[highlight] : predictions[0];
      if (pick) selectPrediction(pick);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleBlur = () => {
    // Defer so an in-flight click on a suggestion can register first.
    setTimeout(() => setIsOpen(false), 180);
  };

  return (
    <div className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => { if (predictions.length > 0) setIsOpen(true); }}
        placeholder={apiReady ? placeholder : "Loading address lookup…"}
        required={required}
        autoComplete="off"
        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-10 text-black ${className}`}
        data-testid={props["data-testid"] || "address-autocomplete-input"}
        {...props}
      />

      {/* Loading spinner inside the input */}
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400 pointer-events-none" />
      )}

      {/* Suggestions dropdown — rendered in-tree so it's always above the
          dialog content automatically (same stacking context). */}
      {isOpen && predictions.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full mt-1 z-[60] max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg py-1"
          role="listbox"
          data-testid="address-autocomplete-suggestions"
        >
          {predictions.map((p, i) => {
            const main = p.structured_formatting?.main_text || p.description;
            const secondary = p.structured_formatting?.secondary_text || "";
            return (
              <li
                key={p.place_id}
                role="option"
                aria-selected={i === highlight}
                onMouseDown={(e) => {
                  // mousedown (not click) so it fires before blur hides us.
                  e.preventDefault();
                  selectPrediction(p);
                }}
                onMouseEnter={() => setHighlight(i)}
                className={`flex items-start gap-2 px-3 py-2 text-sm cursor-pointer ${
                  i === highlight ? "bg-orange-50" : "hover:bg-gray-50"
                }`}
                data-testid={`address-suggestion-${i}`}
              >
                <MapPin className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{main}</div>
                  {secondary && (
                    <div className="text-xs text-gray-500 truncate">{secondary}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
